import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../../shared/ui/Footer';
import StatusActionButton from '../../../shared/ui/buttons/StatusActionButton';
import config from '../../../config/config';
import './OnboardingScreen.css';

// Fallback значения на случай, если config не загрузился
const FALLBACK_CONFIG = {
  app: {
    loginRoute: '/login'
  }
};

// Используем config или fallback
const currentConfig = config || FALLBACK_CONFIG;

const OnboardingScreen: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="onboarding-container">
      <div className="beta-notice-banner">
        <i className="ri-flask-line" />
        <span>
          Проект находится в стадии разработки и закрытого бета-тестирования.
          Регистрация доступна только по приглашению, возможны ошибки и изменения функциональности.{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Подробнее</a>
        </span>
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title animated-title">
              <span className="title-letter" style={{ animationDelay: '0.1s' }}>R</span>
              <span className="title-letter" style={{ animationDelay: '0.2s' }}>y</span>
              <span className="title-letter" style={{ animationDelay: '0.3s' }}>a</span>
              <span className="title-letter" style={{ animationDelay: '0.4s' }}>s</span>
              <span className="title-letter" style={{ animationDelay: '0.5s' }}>k</span>
              <span className="title-letter" style={{ animationDelay: '0.6s' }}>o</span>
              <span className="title-letter" style={{ animationDelay: '0.7s' }}>v</span>
              <span className="title-letter" style={{ animationDelay: '0.8s' }}>.</span>
              <span className="title-letter" style={{ animationDelay: '0.9s' }}>p</span>
              <span className="title-letter" style={{ animationDelay: '1.0s' }}>r</span>
              <span className="title-letter" style={{ animationDelay: '1.1s' }}>o</span>
            </h1>
            <p className="hero-subtitle fade-in" style={{ animationDelay: '1.5s' }}>
              Комплексная система управления ремонтом квартир
            </p>
            <p className="hero-description fade-in" style={{ animationDelay: '1.8s' }}>
              Контролируйте этапы ремонта, учитывайте рабочее время, управляйте материалами 
              и получайте точную финансовую отчетность в реальном времени.
            </p>
            
            <div className="hero-actions fade-in" style={{ animationDelay: '2.1s' }}>
              <StatusActionButton 
                onClick={() => navigate(currentConfig.app.loginRoute)} 
                variant="primary"
                size="large"
                icon="ri-login-box-line"
                className="pulse-button"
              >
                Войти в систему
              </StatusActionButton>
            </div>
          </div>
          
          <div className="hero-image fade-in" style={{ animationDelay: '1.2s' }}>
            <div className="hero-illustration">
              <div className="floating-icons">
                <i className="ri-home-line icon-1"></i>
                <i className="ri-tools-line icon-2"></i>
                <i className="ri-line-chart-line icon-3"></i>
                <i className="ri-time-line icon-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Возможности системы</h2>
            <p className="section-subtitle">Все необходимые инструменты для эффективного управления ремонтными проектами</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-time-line"></i>
              </div>
              <h3>Учет рабочего времени</h3>
              <p>Точный учет часов работы бригад с автоматическим расчетом зарплаты и аналитикой производительности</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-hammer-line"></i>
              </div>
              <h3>Контроль этапов</h3>
              <p>Отслеживание прогресса по каждому этапу работ с управлением сроками и планированием задач</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-truck-line"></i>
              </div>
              <h3>Управление материалами</h3>
              <p>Контроль материалов на объектах, планирование закупок и учет расходов на снабжение</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-line-chart-line"></i>
              </div>
              <h3>Финансовый контроль</h3>
              <p>Расчет расходов на людей, учет доходов с объектов и контроль рентабельности проектов</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-smartphone-line"></i>
              </div>
              <h3>Мобильный доступ</h3>
              <p>Удобный интерфейс для работы на планшетах и телефонах с быстрым вводом данных</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="ri-bar-chart-line"></i>
              </div>
              <h3>Детальная отчетность</h3>
              <p>Формирование отчетов по объектам, бригадам и общая статистика с графиками и диаграммами</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Готовы начать?</h2>
            <p>Присоединяйтесь к закрытому бета-тестированию Ryaskov.pro по приглашению</p>
            
            <StatusActionButton 
              onClick={() => navigate(currentConfig.app.loginRoute)} 
              variant="secondary"
              size="large"
              icon="ri-rocket-line"
              className="glow-button"
            >
              Войти в систему
            </StatusActionButton>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default OnboardingScreen;
